import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Trophy, Zap, Target } from 'lucide-react';
import BirdMascot from './BirdMascot';
import BirdAvatar from './BirdAvatar';

const HalfwayModal = ({ score, correctCount, totalQuestions, nickname, avatarId, onContinue }) => {
  const questionsCompleted = Math.floor(totalQuestions / 2);
  const accuracy = Math.round((correctCount / questionsCompleted) * 100);
  
  // Bird-themed motivational messages based on performance
  const birdMessage = useMemo(() => {
    let messages;
    if (accuracy >= 90) {
      messages = [
        "This bird is SOARING! You're a natural detective!",
        "Flawless feathers! You're pecking all the right answers!",
        "You're flying higher than a jailbird on parole! Spectacular!",
        "Early bird gets the worm, and you're getting ALL the points!"
      ];
    } else if (accuracy >= 70) {
      messages = [
        "Nice wingspan! You're gliding through these questions!",
        "Solid flight pattern! Keep those wings steady!",
        "You're no birdbrain! Good work, detective!",
        "Your detective instincts are taking flight!"
      ];
    } else if (accuracy >= 50) {
      messages = [
        "Well, at least you're not flying into windows... yet.",
        "Your detective skills are... fluttering. Maybe try harder?",
        "Even a blind bird finds a worm sometimes. Keep trying!",
        "Not quite eagle-eyed, are we? Time to focus!"
      ];
    } else {
      messages = [
        "Did someone clip your wings? This is rough, detective.",
        "Even pigeons could do better than this. Come on!",
        "Are you guessing with your eyes closed? Wake up, bird!",
        "This jailbird's going back to the cage at this rate!"
      ];
    }
    // Use score as a deterministic seed for message selection
    const messageIndex = (score + correctCount) % messages.length;
    return messages[messageIndex];
  }, [accuracy, score, correctCount]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-gradient-card border-2 border-primary/30 rounded-2xl p-8 max-w-lg w-full text-center"
      >
        {/* Player Avatar and Nickname */}
        {nickname && avatarId && (
          <div className="flex items-center justify-center gap-3 mb-6">
            <BirdAvatar avatarId={avatarId} size={60} />
            <span className="text-xl font-heading font-bold text-gradient-primary">{nickname}</span>
          </div>
        )}

        <h2 className="text-3xl md:text-4xl font-heading font-bold mb-2">
          Halfway There!
        </h2>
        
        <p className="text-xl text-primary mb-6 font-semibold">{birdMessage}</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-lg bg-muted/50">
            <Trophy className="w-6 h-6 text-gold mx-auto mb-2" />
            <div className="text-2xl font-heading font-bold text-gradient-primary">{score}</div>
            <div className="text-xs text-muted-foreground">Score</div>
          </div>
          
          <div className="p-4 rounded-lg bg-muted/50">
            <Target className="w-6 h-6 text-primary mx-auto mb-2" />
            <div className="text-2xl font-heading font-bold">{correctCount}/{questionsCompleted}</div>
            <div className="text-xs text-muted-foreground">Correct</div>
          </div>
          
          <div className="p-4 rounded-lg bg-muted/50">
            <Zap className="w-6 h-6 text-accent mx-auto mb-2" />
            <div className="text-2xl font-heading font-bold text-gradient-accent">{accuracy}%</div>
            <div className="text-xs text-muted-foreground">Accuracy</div>
          </div>
        </div>

        <p className="text-muted-foreground mb-6">
          You&apos;ve completed {questionsCompleted} out of {totalQuestions} questions.
          <br />
          Keep up the great work!
        </p>

        <Button
          size="lg"
          onClick={onContinue}
          className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-lg w-full"
        >
          Continue to Second Half
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default HalfwayModal;
